import type { JSX } from 'react';
import { useBuildStamp } from './buildStamp.js';

/**
 * La bande "app deprecated" : quand l'app ouverte a ete mise au rebut, une
 * bande le dit en haut de l'ecran, en permanence, tant qu'on est dedans.
 *
 * POURQUOI ELLE EXISTE. Le marqueur `[Deprecated]` etait autrefois ecrit A LA
 * MAIN dans le nom de l'app, a trois endroits, ce qui le rendait faux des qu'on
 * en oubliait un. Il est devenu un champ de la fiche (`deprecated` +
 * `deprecatedSince`, docs/PLAYBOOK-APP.md section 4), donc verifiable par la CI, mais
 * plus rien ne le disait A L'ECRAN. Cette bande referme ce trou : l'information
 * revient sous les yeux de l'utilisateur, et elle vient cette fois d'une source
 * unique.
 *
 * PERMANENTE, ET C'EST LA DIFFERENCE AVEC LA LIGNE DE VERSION. La ligne de
 * version est un outil de developpement : elle vit dans `dev/`, derriere une
 * case a cocher, et elle est absente du build de production. Celle-ci s'affiche
 * chez un client, sans reglage : ouvrir une app morte sans le savoir est
 * precisement ce qu'il faut eviter.
 *
 * D'OU VIENT L'INFORMATION. Du tampon de build (`/taki-build.json`), qui porte
 * pour chaque app embarquee son nom, sa version, son `deprecated` et son
 * `deprecatedSince`, lus dans les fiches AU BUILD. Consequence a connaitre : une
 * app passee deprecated apres le dernier build ne sera signalee qu'au build
 * suivant. C'est le prix d'une source unique, et il est juste : le tampon decrit
 * ce qui TOURNE, pas ce qui est ecrit dans le depot a cet instant.
 *
 * CE QU'ELLE NE DEMANDE A PERSONNE. Aucune app ne passe de prop, aucun registre
 * ne declare quoi que ce soit, le generateur d'apps n'a rien a apprendre. C'est
 * deliberé : la lecon la plus couteuse de l'historique du depot est qu'un socle
 * qui exige d'etre cable par chaque consommateur finit toujours par avoir un
 * consommateur non cable (PR 52). Ici le shell sait deja quelle app est ouverte,
 * et le tampon sait deja si elle est morte.
 *
 * OU ELLE NE S'AFFICHE PAS, ET C'EST VOULU : uniquement DANS une app. Sur le
 * bureau et dans le hub Parametres, il n'y a pas d'app ouverte, donc rien a
 * signaler ; et surtout, le conteneur en colonne de `TakiShell` ne defile pas,
 * donc une bande permanente au-dessus du bureau ecreterait la fenetre Home sur
 * un ecran bas. Le montage dans la seule branche `inApp` evite ce probleme par
 * construction.
 *
 * MOTION : aucune. La bande est la ou elle n'est pas ; un fondu a chaque
 * navigation se lirait comme un flash (doctrine motion, docs/PLAYBOOK-APP.md section 6).
 */

export interface DeprecatedBandProps {
  /** L'app ouverte, telle que le shell la lit dans le chemin (`/app/<id>`). */
  appId: string | undefined;
}

export default function DeprecatedBand({ appId }: DeprecatedBandProps): JSX.Element | null {
  const state = useBuildStamp();

  if (appId == null || state.kind !== 'found') return null;

  const app = state.stamp.apps.find((entry) => entry.id === appId);
  if (app == null || app.deprecated !== true) return null;

  // Le "depuis" n'est affiche que s'il est connu : une bande qui dirait
  // "deprecated depuis null" serait pire que muette.
  const depuis =
    app.deprecatedSince != null
      ? ` depuis Taki ${app.deprecatedSince.replace(/\.0$/, '')}`
      : '';

  return (
    <div
      role="status"
      // shrink-0 : la bande garde sa hauteur, c'est la surface de travail qui
      // absorbe la difference (conteneur en colonne de TakiShell).
      // COULEUR : la famille `danger` en version PALE, la seule palette d'etat
      // du design system. Une app morte n'est pas une erreur, mais c'est un
      // avertissement, et il doit se lire sans hesitation. Si une palette
      // d'avertissement dediee est un jour ajoutee aux tokens, c'est ici qu'elle
      // remplace celle-ci, en un seul endroit.
      className="flex h-7 w-full shrink-0 items-center justify-center gap-1.5 overflow-hidden border-b border-danger-border bg-danger-tint px-3 text-xs leading-none text-danger"
    >
      <span className="truncate font-medium">{app.name}</span>
      <span className="truncate">
        est deprecated{depuis} : cette app n&apos;est plus maintenue et peut disparaitre.
      </span>
    </div>
  );
}
